import { User, MapPin, CreditCard, Save, X } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import CollapsibleSection from '@/components/ui/CollapsibleSection'
import { Input } from '@/components/ui/input'
import { useUpdateBeneficiary } from '@/hooks/useCpn'
import { CpnBeneficiaryUser } from '@/types/cpn'
import { logger } from '@/utils/logger'
import { emailRegex } from '@/utils/validation'

interface UserInfoFormData {
  firstName: string
  lastName: string
  street: string
  city: string
  stateProvince: string
  postalCode: string
  country: string
}

interface MetadataFormData {
  ACCOUNT_NUMBER: string
  ACCOUNT_TYPE: string
  BANK_NAME: string
  BANK_CODE: string
  BANK_COUNTRY: string
  CLABE: string
  RECIPIENT_ID_NUMBER: string
  SWIFT_CODE: string
  BENEFICIARY_DATE_OF_BIRTH: string
  BENEFICIARY_NATIONALITY: string
  BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER: string
  BENEFICIARY_PHONE_NUMBER: string
  BENEFICIARY_EMAIL: string
}

interface FormErrors {
  [key: string]: string | undefined
}

interface BeneficiaryEditDrawerProps {
  isOpen: boolean
  onClose: () => void
  beneficiary: CpnBeneficiaryUser
  onBeneficiaryUpdated?: () => void
  missingRequirements?: {
    beneficiaryAccount?: string[]
    travelRule?: string[]
  }
}

const BeneficiaryEditDrawer = ({
  isOpen,
  onClose,
  beneficiary,
  onBeneficiaryUpdated,
  missingRequirements,
}: BeneficiaryEditDrawerProps) => {
  // Helper function to safely extract metadata values
  const getMetadataValue = (
    metadata: Record<string, unknown> | undefined,
    key: string
  ): string => {
    if (!metadata || typeof metadata[key] !== 'string') {
      return ''
    }
    return metadata[key] as string
  }

  // Helper to check if a field is required (missing)
  const isFieldRequired = (fieldName: string): boolean => {
    if (!missingRequirements) return true // Show all fields if no requirements specified

    const allMissing = [
      ...(missingRequirements.beneficiaryAccount || []),
      ...(missingRequirements.travelRule || []),
    ]

    return allMissing.includes(fieldName)
  }

  // Check if address is required based on missing requirements
  const isAddressRequired =
    isFieldRequired('BENEFICIARY_ADDRESS') || !missingRequirements // If no requirements specified, assume address is required

  // Helper to check if user info fields should be disabled (when already filled)
  const isUserInfoFieldDisabled = (
    fieldName: keyof UserInfoFormData
  ): boolean => {
    const originalValue = beneficiary[fieldName]
    return !!(originalValue && originalValue.trim())
  }

  const [editFormData, setEditFormData] = useState<UserInfoFormData>({
    firstName: beneficiary.firstName || '',
    lastName: beneficiary.lastName || '',
    street: beneficiary.street || '',
    city: beneficiary.city || '',
    stateProvince: beneficiary.stateProvince || '',
    postalCode: beneficiary.postalCode || '',
    country: beneficiary.country || '',
  })

  const [editMetadataData, setEditMetadataData] = useState<MetadataFormData>({
    ACCOUNT_NUMBER: getMetadataValue(beneficiary.metadata, 'ACCOUNT_NUMBER'),
    ACCOUNT_TYPE: getMetadataValue(beneficiary.metadata, 'ACCOUNT_TYPE'),
    BANK_NAME: getMetadataValue(beneficiary.metadata, 'BANK_NAME'),
    BANK_CODE: getMetadataValue(beneficiary.metadata, 'BANK_CODE'),
    BANK_COUNTRY: getMetadataValue(beneficiary.metadata, 'BANK_COUNTRY'),
    CLABE: getMetadataValue(beneficiary.metadata, 'CLABE'),
    RECIPIENT_ID_NUMBER: getMetadataValue(
      beneficiary.metadata,
      'RECIPIENT_ID_NUMBER'
    ),
    SWIFT_CODE: getMetadataValue(beneficiary.metadata, 'SWIFT_CODE'),
    BENEFICIARY_DATE_OF_BIRTH: getMetadataValue(
      beneficiary.metadata,
      'BENEFICIARY_DATE_OF_BIRTH'
    ),
    BENEFICIARY_NATIONALITY: getMetadataValue(
      beneficiary.metadata,
      'BENEFICIARY_NATIONALITY'
    ),
    BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER: getMetadataValue(
      beneficiary.metadata,
      'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER'
    ),
    BENEFICIARY_PHONE_NUMBER: getMetadataValue(
      beneficiary.metadata,
      'BENEFICIARY_PHONE_NUMBER'
    ),
    BENEFICIARY_EMAIL: getMetadataValue(
      beneficiary.metadata,
      'BENEFICIARY_EMAIL'
    ),
  })

  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isUpdating, setIsUpdating] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const updateBeneficiaryMutation = useUpdateBeneficiary()

  // Validation functions
  const validateUserInfoField = (
    name: keyof UserInfoFormData,
    value: string
  ): string | undefined => {
    const trimmedValue = value.trim()

    switch (name) {
      case 'firstName':
        if (!trimmedValue) return 'First name is required'
        if (trimmedValue.length < 2)
          return 'First name must be at least 2 characters'
        if (trimmedValue.length > 50)
          return 'First name must be less than 50 characters'
        if (!/^[\p{L}\s'-]+$/u.test(trimmedValue))
          return 'First name can only contain letters, spaces, hyphens, and apostrophes'
        break
      case 'lastName':
        if (!trimmedValue) return 'Last name is required'
        if (trimmedValue.length < 2)
          return 'Last name must be at least 2 characters'
        if (trimmedValue.length > 50)
          return 'Last name must be less than 50 characters'
        if (!/^[\p{L}\s'-]+$/u.test(trimmedValue))
          return 'Last name can only contain letters, spaces, hyphens, and apostrophes'
        break
      case 'street':
        if (!trimmedValue) return 'Street address is required'
        if (trimmedValue.length < 5)
          return 'Street address must be at least 5 characters'
        if (trimmedValue.length > 100)
          return 'Street address must be less than 100 characters'
        break
      case 'city':
        if (!trimmedValue) return 'City is required'
        if (trimmedValue.length < 2) return 'City must be at least 2 characters'
        if (trimmedValue.length > 50)
          return 'City must be less than 50 characters'
        if (!/^[\p{L}\s'-]+$/u.test(trimmedValue))
          return 'City can only contain letters, spaces, hyphens, and apostrophes'
        break
      case 'stateProvince':
        if (!trimmedValue) return 'State/Province is required'
        if (trimmedValue.length < 2)
          return 'State/Province must be at least 2 characters'
        if (trimmedValue.length > 50)
          return 'State/Province must be less than 50 characters'
        break
      case 'postalCode':
        if (!trimmedValue) return 'Postal code is required'
        if (trimmedValue.length < 3)
          return 'Postal code must be at least 3 characters'
        if (trimmedValue.length > 10)
          return 'Postal code must be less than 10 characters'
        // Basic postal code format validation
        if (!/^[A-Za-z0-9\s-]+$/.test(trimmedValue))
          return 'Postal code can only contain letters, numbers, spaces, and hyphens'
        break
      case 'country':
        if (!trimmedValue) return 'Country is required'
        if (value.length !== 2)
          return 'Country must be 2-letter code (e.g., CA, US, HK)'
        if (!/^[A-Z]{2}$/.test(value))
          return 'Country code must be uppercase letters (e.g., CA, US, HK)'
        break
    }
    return undefined
  }

  const validateMetadataField = (
    name: keyof MetadataFormData,
    value: string
  ): string | undefined => {
    const trimmedValue = value.trim()

    // Check if field is required based on missingRequirements
    const fieldIsRequired = isFieldRequired(name)

    if (!trimmedValue) {
      if (fieldIsRequired) {
        return 'This field is required'
      }
      return undefined // Optional fields can be empty
    }

    switch (name) {
      case 'ACCOUNT_NUMBER':
        if (trimmedValue.length < 5)
          return 'Account number must be at least 5 characters'
        if (trimmedValue.length > 30)
          return 'Account number must be less than 30 characters'
        if (!/^[A-Za-z0-9-]+$/.test(trimmedValue))
          return 'Account number can only contain letters, numbers, and hyphens'
        break
      case 'ACCOUNT_TYPE':
        if (trimmedValue.length < 2)
          return 'Account type must be at least 2 characters'
        if (trimmedValue.length > 50)
          return 'Account type must be less than 50 characters'
        break
      case 'BANK_NAME':
        if (trimmedValue.length < 2)
          return 'Bank name must be at least 2 characters'
        if (trimmedValue.length > 100)
          return 'Bank name must be less than 100 characters'
        if (!/^[a-zA-Z\s&.-]+$/.test(trimmedValue))
          return 'Bank name can only contain letters, spaces, and common punctuation (&, ., -)'
        if (/^\d+$/.test(trimmedValue))
          return 'Bank name cannot be only numbers'
        break
      case 'BANK_CODE':
        if (trimmedValue.length < 3)
          return 'Bank code must be at least 3 characters'
        if (trimmedValue.length > 25)
          return 'Bank code must be less than 25 characters'
        if (!/^[A-Za-z0-9]+$/.test(trimmedValue))
          return 'Bank code can only contain letters and numbers'
        break
      case 'BANK_COUNTRY':
        if (value.length !== 2)
          return 'Bank country must be 2-letter code (e.g., HK, US)'
        if (!/^[A-Z]{2}$/.test(value))
          return 'Bank country code must be uppercase letters (e.g., HK, US)'
        break
      case 'CLABE':
        if (trimmedValue.length !== 18)
          return 'CLABE number must be exactly 18 digits'
        if (!/^\d{18}$/.test(trimmedValue))
          return 'CLABE number can only contain digits'
        break
      case 'RECIPIENT_ID_NUMBER':
        if (trimmedValue.length < 5)
          return 'Recipient ID must be at least 5 characters'
        if (trimmedValue.length > 50)
          return 'Recipient ID must be less than 50 characters'
        if (!/^[A-Za-z0-9-]+$/.test(trimmedValue))
          return 'Recipient ID can only contain letters, numbers, and hyphens'
        break
      case 'SWIFT_CODE':
        if (trimmedValue.length < 8 || trimmedValue.length > 11)
          return 'SWIFT code must be 8-11 characters'
        if (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value))
          return 'Invalid SWIFT code format (e.g., HKBAHKHHXXX)'
        break
      case 'BENEFICIARY_DATE_OF_BIRTH': {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue))
          return 'Date must be in YYYY-MM-DD format'
        const date = new Date(trimmedValue)
        if (isNaN(date.getTime())) return 'Invalid date'
        if (date > new Date()) return 'Date of birth cannot be in the future'
        break
      }
      case 'BENEFICIARY_NATIONALITY':
        if (value.length !== 2)
          return 'Nationality must be 2-letter code (e.g., IN, US)'
        if (!/^[A-Z]{2}$/.test(value))
          return 'Nationality code must be uppercase letters'
        break
      case 'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER':
        if (trimmedValue.length < 5)
          return 'ID number must be at least 5 characters'
        if (trimmedValue.length > 50)
          return 'ID number must be less than 50 characters'
        break
      case 'BENEFICIARY_PHONE_NUMBER':
        if (!/^\+?[\d\s-()]+$/.test(trimmedValue))
          return 'Invalid phone number format'
        if (trimmedValue.replace(/\D/g, '').length < 10)
          return 'Phone number must be at least 10 digits'
        break
      case 'BENEFICIARY_EMAIL':
        if (!emailRegex.test(trimmedValue)) return 'Invalid email format'
        break
    }
    return undefined
  }

  // Form handlers
  const handleUserInfoChange = (
    name: keyof UserInfoFormData,
    value: string
  ) => {
    setEditFormData((prev) => ({ ...prev, [name]: value }))

    if (touched[name]) {
      const error = validateUserInfoField(name, value)
      setFormErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const handleMetadataChange = (
    name: keyof MetadataFormData,
    value: string
  ) => {
    setEditMetadataData((prev) => ({ ...prev, [name]: value }))

    if (touched[name]) {
      const error = validateMetadataField(name, value)
      setFormErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const handleInputBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }))

    if (name in editFormData) {
      const error = validateUserInfoField(
        name as keyof UserInfoFormData,
        editFormData[name as keyof UserInfoFormData]
      )
      setFormErrors((prev) => ({ ...prev, [name]: error }))
    } else if (name in editMetadataData) {
      const error = validateMetadataField(
        name as keyof MetadataFormData,
        editMetadataData[name as keyof MetadataFormData]
      )
      setFormErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const validateForm = (): {
    isValid: boolean
    errors: FormErrors
    errorCount: number
  } => {
    const newErrors: FormErrors = {}
    let isValid = true
    let firstErrorField: string | null = null

    // Validate user info fields
    ;(Object.keys(editFormData) as Array<keyof UserInfoFormData>).forEach(
      (key) => {
        const addressFields: Array<keyof UserInfoFormData> = [
          'street',
          'city',
          'stateProvince',
          'postalCode',
          'country',
        ]
        if (!isAddressRequired && addressFields.includes(key)) {
          return
        }

        const error = validateUserInfoField(key, editFormData[key])
        if (error) {
          newErrors[key] = error
          isValid = false
          if (!firstErrorField) firstErrorField = key
        }
      }
    )

    // Validate metadata fields - ONLY validate fields that are required (visible in the form)
    ;(Object.keys(editMetadataData) as Array<keyof MetadataFormData>).forEach(
      (key) => {
        const value = editMetadataData[key]
        const fieldIsRequired = isFieldRequired(key)

        // Only validate required fields that are shown in the form
        if (fieldIsRequired) {
          const error = validateMetadataField(key, value)
          if (error) {
            newErrors[key] = error
            isValid = false
            if (!firstErrorField) firstErrorField = key
          }
        }
      }
    )
    setFormErrors(newErrors)
    setTouched({
      ...Object.keys(editFormData).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      ),
      ...Object.keys(editMetadataData).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      ),
    })

    // Scroll to first error if validation fails
    if (!isValid && firstErrorField) {
      setTimeout(() => {
        const errorElement = document.querySelector(
          `[name="${firstErrorField}"]`
        )
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    }

    const errorCount = Object.keys(newErrors).filter(
      (key) => newErrors[key]
    ).length

    return { isValid, errors: newErrors, errorCount }
  }

  const handleSave = async () => {
    // Clear any previous save errors
    setSaveError(null)

    const validation = validateForm()

    if (!validation.isValid) {
      // Show specific error messages
      const errorList = Object.entries(validation.errors)
        .filter(([_, error]) => error)
        .map(([field, error]) => {
          // Make field names more readable
          const readableField = field
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (l) => l.toUpperCase())

          // Add helper text for optional fields
          const isRequired = isFieldRequired(field)
          const helperText = !isRequired
            ? ' (or clear this field if not needed)'
            : ''

          return `• ${readableField}: ${error}${helperText}`
        })
        .join('\n')

      setSaveError(
        errorList || 'Please fix the validation errors before saving.'
      )
      return
    }

    setIsUpdating(true)

    try {
      // Update user info
      await updateBeneficiaryMutation.mutateAsync({
        beneficiaryId: beneficiary.uuid,
        data: {
          updateType: 'user',
          ...editFormData,
        },
      })

      // Update metadata (filter out empty values)
      const metadata = Object.fromEntries(
        Object.entries(editMetadataData).filter(
          ([_, value]) => value.trim() !== ''
        )
      )

      if (Object.keys(metadata).length > 0) {
        await updateBeneficiaryMutation.mutateAsync({
          beneficiaryId: beneficiary.uuid,
          data: {
            updateType: 'metadata',
            metadata,
          },
        })
      }

      onClose()

      // Notify parent component that Recipient was updated
      if (onBeneficiaryUpdated) {
        onBeneficiaryUpdated()
      }
    } catch (error: unknown) {
      logger.error('Failed to update Recipient', error)

      // Extract detailed error message
      let errorMessage =
        'Failed to update recipient information. Please try again.'

      if (error && typeof error === 'object') {
        const err = error as {
          response?: { data?: string | { message?: string; error?: string } }
          message?: string
        }
        if (err?.response?.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error
          }
        } else if (err?.message) {
          errorMessage = err.message
        }
      }

      setSaveError(errorMessage)

      // Scroll to top to show error
      setTimeout(() => {
        const errorElement = document.querySelector('[data-save-error]')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    } finally {
      setIsUpdating(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-t-lg w-full max-h-[80%] flex flex-col border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Edit Recipient
            </h3>
            <Button variant="ghost" size="sm" className="p-1" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-left">
            Update recipient information to complete your payment
          </p>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Save Error Display */}
          {saveError && (
            <div
              data-save-error
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4"
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-red-600 dark:text-red-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Validation Error{saveError.includes('•') ? 's' : ''}
                  </h3>
                  <div className="text-sm text-red-700 dark:text-red-300 mt-1 whitespace-pre-line">
                    {saveError}
                  </div>
                </div>
                <button
                  onClick={() => setSaveError(null)}
                  className="flex-shrink-0 text-red-400 hover:text-red-600 dark:hover:text-red-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Personal Information - Collapsible */}
          <CollapsibleSection
            title="Personal Information"
            icon={<User className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
            defaultOpen={false}
            className="bg-white dark:bg-gray-800"
          >
            <div className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name *
                  </label>
                  <Input
                    name="firstName"
                    value={editFormData.firstName}
                    onChange={(e) =>
                      handleUserInfoChange('firstName', e.target.value)
                    }
                    onBlur={() => handleInputBlur('firstName')}
                    placeholder="John"
                    disabled={isUserInfoFieldDisabled('firstName')}
                    className={
                      formErrors.firstName
                        ? 'border-red-500 dark:border-red-400'
                        : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                    }
                    aria-invalid={!!formErrors.firstName}
                    aria-describedby={
                      formErrors.firstName ? 'firstName-error' : undefined
                    }
                  />
                  {formErrors.firstName && (
                    <p
                      id="firstName-error"
                      className="text-xs text-red-600 dark:text-red-400 mt-1"
                      role="alert"
                    >
                      {formErrors.firstName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name *
                  </label>
                  <Input
                    name="lastName"
                    value={editFormData.lastName}
                    onChange={(e) =>
                      handleUserInfoChange('lastName', e.target.value)
                    }
                    onBlur={() => handleInputBlur('lastName')}
                    placeholder="Doe"
                    disabled={isUserInfoFieldDisabled('lastName')}
                    className={
                      formErrors.lastName
                        ? 'border-red-500 dark:border-red-400'
                        : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                    }
                    aria-invalid={!!formErrors.lastName}
                    aria-describedby={
                      formErrors.lastName ? 'lastName-error' : undefined
                    }
                  />
                  {formErrors.lastName && (
                    <p
                      id="lastName-error"
                      className="text-xs text-red-600 dark:text-red-400 mt-1"
                      role="alert"
                    >
                      {formErrors.lastName}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {isAddressRequired && (
            <CollapsibleSection
              title="Address Information"
              icon={
                <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              }
              defaultOpen={false}
              className="bg-white dark:bg-gray-800"
            >
              <div className="space-y-3 pt-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Street Address *
                  </label>
                  <Input
                    value={editFormData.street}
                    onChange={(e) =>
                      handleUserInfoChange('street', e.target.value)
                    }
                    onBlur={() => handleInputBlur('street')}
                    placeholder="123 Main Street"
                    disabled={isUserInfoFieldDisabled('street')}
                    className={
                      formErrors.street
                        ? 'border-red-500 dark:border-red-400'
                        : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                    }
                  />
                  {formErrors.street && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {formErrors.street}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      City *
                    </label>
                    <Input
                      value={editFormData.city}
                      onChange={(e) =>
                        handleUserInfoChange('city', e.target.value)
                      }
                      onBlur={() => handleInputBlur('city')}
                      placeholder="Toronto"
                      disabled={isUserInfoFieldDisabled('city')}
                      className={
                        formErrors.city
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.city && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.city}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      State/Province *
                    </label>
                    <Input
                      value={editFormData.stateProvince}
                      onChange={(e) =>
                        handleUserInfoChange('stateProvince', e.target.value)
                      }
                      onBlur={() => handleInputBlur('stateProvince')}
                      placeholder="Ontario"
                      disabled={isUserInfoFieldDisabled('stateProvince')}
                      className={
                        formErrors.stateProvince
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.stateProvince && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.stateProvince}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Postal Code *
                    </label>
                    <Input
                      value={editFormData.postalCode}
                      onChange={(e) =>
                        handleUserInfoChange('postalCode', e.target.value)
                      }
                      onBlur={() => handleInputBlur('postalCode')}
                      placeholder="M5V 3A8"
                      disabled={isUserInfoFieldDisabled('postalCode')}
                      className={
                        formErrors.postalCode
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.postalCode && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.postalCode}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Country Code *
                    </label>
                    <Input
                      value={editFormData.country}
                      onChange={(e) =>
                        handleUserInfoChange(
                          'country',
                          e.target.value.toUpperCase()
                        )
                      }
                      onBlur={() => handleInputBlur('country')}
                      placeholder="CA"
                      maxLength={2}
                      disabled={isUserInfoFieldDisabled('country')}
                      className={
                        formErrors.country
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.country && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.country}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Bank Information - Conditionally visible */}
          {isFieldRequired('ACCOUNT_NUMBER') ||
          isFieldRequired('ACCOUNT_TYPE') ||
          isFieldRequired('BANK_CODE') ||
          isFieldRequired('BANK_NAME') ||
          isFieldRequired('BANK_COUNTRY') ||
          isFieldRequired('SWIFT_CODE') ||
          isFieldRequired('CLABE') ||
          isFieldRequired('RECIPIENT_ID_NUMBER') ? (
            <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
              <div className="flex items-center space-x-2 mb-3">
                <CreditCard className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <h3 className="font-medium text-gray-900 dark:text-white">
                  Bank Information
                </h3>
              </div>

              <div className="space-y-3">
                {isFieldRequired('ACCOUNT_NUMBER') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Account Number *
                    </label>
                    <Input
                      name="ACCOUNT_NUMBER"
                      value={editMetadataData.ACCOUNT_NUMBER}
                      onChange={(e) =>
                        handleMetadataChange('ACCOUNT_NUMBER', e.target.value)
                      }
                      onBlur={() => handleInputBlur('ACCOUNT_NUMBER')}
                      placeholder="e.g., 1234567890"
                      className={
                        formErrors.ACCOUNT_NUMBER
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                      aria-invalid={!!formErrors.ACCOUNT_NUMBER}
                      aria-describedby={
                        formErrors.ACCOUNT_NUMBER
                          ? 'ACCOUNT_NUMBER-error'
                          : undefined
                      }
                    />
                    {formErrors.ACCOUNT_NUMBER && (
                      <p
                        id="ACCOUNT_NUMBER-error"
                        className="text-xs text-red-600 dark:text-red-400 mt-1"
                        role="alert"
                      >
                        {formErrors.ACCOUNT_NUMBER}
                      </p>
                    )}
                  </div>
                )}

                {isFieldRequired('ACCOUNT_TYPE') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Account Type *
                    </label>
                    <Input
                      name="ACCOUNT_TYPE"
                      value={editMetadataData.ACCOUNT_TYPE}
                      onChange={(e) =>
                        handleMetadataChange('ACCOUNT_TYPE', e.target.value)
                      }
                      onBlur={() => handleInputBlur('ACCOUNT_TYPE')}
                      placeholder="e.g., Savings, Current, Checking"
                      className={
                        formErrors.ACCOUNT_TYPE
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                      aria-invalid={!!formErrors.ACCOUNT_TYPE}
                      aria-describedby={
                        formErrors.ACCOUNT_TYPE
                          ? 'ACCOUNT_TYPE-error'
                          : undefined
                      }
                    />
                    {formErrors.ACCOUNT_TYPE && (
                      <p
                        id="ACCOUNT_TYPE-error"
                        className="text-xs text-red-600 dark:text-red-400 mt-1"
                        role="alert"
                      >
                        {formErrors.ACCOUNT_TYPE}
                      </p>
                    )}
                  </div>
                )}

                {isFieldRequired('BANK_NAME') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Bank Name *
                    </label>
                    <Input
                      value={editMetadataData.BANK_NAME}
                      onChange={(e) =>
                        handleMetadataChange('BANK_NAME', e.target.value)
                      }
                      onBlur={() => handleInputBlur('BANK_NAME')}
                      placeholder="e.g., HSBC, Bank of America"
                      className={
                        formErrors.BANK_NAME
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.BANK_NAME && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.BANK_NAME}
                      </p>
                    )}
                  </div>
                )}

                {(isFieldRequired('BANK_CODE') ||
                  isFieldRequired('BANK_COUNTRY')) && (
                  <div className="grid grid-cols-2 gap-3">
                    {isFieldRequired('BANK_CODE') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Bank Code *
                        </label>
                        <Input
                          value={editMetadataData.BANK_CODE}
                          onChange={(e) =>
                            handleMetadataChange('BANK_CODE', e.target.value)
                          }
                          onBlur={() => handleInputBlur('BANK_CODE')}
                          placeholder="e.g., 004, 123"
                          className={
                            formErrors.BANK_CODE
                              ? 'border-red-500 dark:border-red-400'
                              : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                          }
                        />
                        {formErrors.BANK_CODE && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            {formErrors.BANK_CODE}
                          </p>
                        )}
                      </div>
                    )}

                    {isFieldRequired('BANK_COUNTRY') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Bank Country *
                        </label>
                        <Input
                          value={editMetadataData.BANK_COUNTRY}
                          onChange={(e) =>
                            handleMetadataChange(
                              'BANK_COUNTRY',
                              e.target.value.toUpperCase()
                            )
                          }
                          onBlur={() => handleInputBlur('BANK_COUNTRY')}
                          placeholder="HK"
                          maxLength={2}
                          className={
                            formErrors.BANK_COUNTRY
                              ? 'border-red-500 dark:border-red-400'
                              : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                          }
                        />
                        {formErrors.BANK_COUNTRY && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            {formErrors.BANK_COUNTRY}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {isFieldRequired('SWIFT_CODE') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      SWIFT Code *
                    </label>
                    <Input
                      value={editMetadataData.SWIFT_CODE}
                      onChange={(e) =>
                        handleMetadataChange(
                          'SWIFT_CODE',
                          e.target.value.toUpperCase()
                        )
                      }
                      onBlur={() => handleInputBlur('SWIFT_CODE')}
                      placeholder="e.g., HKBAHKHHXXX"
                      maxLength={11}
                      className={
                        formErrors.SWIFT_CODE
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.SWIFT_CODE && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.SWIFT_CODE}
                      </p>
                    )}
                  </div>
                )}

                {isFieldRequired('CLABE') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      CLABE Number *
                    </label>
                    <Input
                      value={editMetadataData.CLABE}
                      onChange={(e) =>
                        handleMetadataChange('CLABE', e.target.value)
                      }
                      onBlur={() => handleInputBlur('CLABE')}
                      placeholder="e.g., 012345678901234567"
                      className={
                        formErrors.CLABE
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.CLABE && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.CLABE}
                      </p>
                    )}
                  </div>
                )}

                {isFieldRequired('RECIPIENT_ID_NUMBER') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Recipient ID Number *
                    </label>
                    <Input
                      value={editMetadataData.RECIPIENT_ID_NUMBER}
                      onChange={(e) =>
                        handleMetadataChange(
                          'RECIPIENT_ID_NUMBER',
                          e.target.value
                        )
                      }
                      onBlur={() => handleInputBlur('RECIPIENT_ID_NUMBER')}
                      placeholder="e.g., National ID, Passport number"
                      className={
                        formErrors.RECIPIENT_ID_NUMBER
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.RECIPIENT_ID_NUMBER && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.RECIPIENT_ID_NUMBER}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ) : null}

          {/* Travel Rule Information - Conditionally visible */}
          {(isFieldRequired('BENEFICIARY_DATE_OF_BIRTH') ||
            isFieldRequired('BENEFICIARY_NATIONALITY') ||
            isFieldRequired('BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER') ||
            isFieldRequired('BENEFICIARY_PHONE_NUMBER') ||
            isFieldRequired('BENEFICIARY_EMAIL')) && (
            <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
              <div className="flex items-center space-x-2 mb-3">
                <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <h3 className="font-medium text-gray-900 dark:text-white">
                  Additional Information
                </h3>
              </div>

              <div className="space-y-3">
                {isFieldRequired('BENEFICIARY_DATE_OF_BIRTH') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Date of Birth *
                    </label>
                    <Input
                      type="date"
                      value={editMetadataData.BENEFICIARY_DATE_OF_BIRTH}
                      onChange={(e) =>
                        handleMetadataChange(
                          'BENEFICIARY_DATE_OF_BIRTH',
                          e.target.value
                        )
                      }
                      onBlur={() =>
                        handleInputBlur('BENEFICIARY_DATE_OF_BIRTH')
                      }
                      className={
                        formErrors.BENEFICIARY_DATE_OF_BIRTH
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.BENEFICIARY_DATE_OF_BIRTH && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.BENEFICIARY_DATE_OF_BIRTH}
                      </p>
                    )}
                  </div>
                )}

                {isFieldRequired('BENEFICIARY_NATIONALITY') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nationality *
                    </label>
                    <Input
                      value={editMetadataData.BENEFICIARY_NATIONALITY}
                      onChange={(e) =>
                        handleMetadataChange(
                          'BENEFICIARY_NATIONALITY',
                          e.target.value.toUpperCase()
                        )
                      }
                      onBlur={() => handleInputBlur('BENEFICIARY_NATIONALITY')}
                      placeholder="e.g., IN, US"
                      maxLength={2}
                      className={
                        formErrors.BENEFICIARY_NATIONALITY
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.BENEFICIARY_NATIONALITY && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.BENEFICIARY_NATIONALITY}
                      </p>
                    )}
                  </div>
                )}

                {isFieldRequired(
                  'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER'
                ) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      National ID Number *
                    </label>
                    <Input
                      value={
                        editMetadataData.BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER
                      }
                      onChange={(e) =>
                        handleMetadataChange(
                          'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER',
                          e.target.value
                        )
                      }
                      onBlur={() =>
                        handleInputBlur(
                          'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER'
                        )
                      }
                      placeholder="e.g., Aadhaar, SSN, Passport"
                      className={
                        formErrors.BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER}
                      </p>
                    )}
                  </div>
                )}

                {isFieldRequired('BENEFICIARY_PHONE_NUMBER') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone Number *
                    </label>
                    <Input
                      type="tel"
                      value={editMetadataData.BENEFICIARY_PHONE_NUMBER}
                      onChange={(e) =>
                        handleMetadataChange(
                          'BENEFICIARY_PHONE_NUMBER',
                          e.target.value
                        )
                      }
                      onBlur={() => handleInputBlur('BENEFICIARY_PHONE_NUMBER')}
                      placeholder="e.g., +91 98765 43210"
                      className={
                        formErrors.BENEFICIARY_PHONE_NUMBER
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.BENEFICIARY_PHONE_NUMBER && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.BENEFICIARY_PHONE_NUMBER}
                      </p>
                    )}
                  </div>
                )}

                {isFieldRequired('BENEFICIARY_EMAIL') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      value={editMetadataData.BENEFICIARY_EMAIL}
                      onChange={(e) =>
                        handleMetadataChange(
                          'BENEFICIARY_EMAIL',
                          e.target.value
                        )
                      }
                      onBlur={() => handleInputBlur('BENEFICIARY_EMAIL')}
                      placeholder="e.g., beneficiary@example.com"
                      className={
                        formErrors.BENEFICIARY_EMAIL
                          ? 'border-red-500 dark:border-red-400'
                          : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                      }
                    />
                    {formErrors.BENEFICIARY_EMAIL && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {formErrors.BENEFICIARY_EMAIL}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
          <Button
            onClick={handleSave}
            disabled={
              // Only disable if there are errors on VISIBLE/EDITABLE fields
              Object.entries(formErrors).some(([field, error]) => {
                // Check if this is a user info field (always visible) OR a required metadata field (visible)
                const isUserInfoField = field in editFormData
                const isRequiredMetadataField = isFieldRequired(field)
                return error && (isUserInfoField || isRequiredMetadataField)
              }) || isUpdating
            }
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving Changes...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Save className="w-4 h-4" />
                <span>Save & Continue Payment</span>
              </div>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default BeneficiaryEditDrawer
